import { CommonModule } from '@angular/common';
import { FALLBACK_INTERVIEW_LANGUAGES, InterviewLanguage } from '@edtech-ui/constants/interview-language.constant';
import { Component, EventEmitter, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Interviewer } from '@edtech-ui/core/models/ai-interview/ai-interviwer';
import { AiInterviewServiceService } from '@edtech-ui/core/services/ai-interview-service/ai-interview-service.service';
import { AiInterviewSessionService } from '@edtech-ui/core/services/ai-interview-session-service/ai-interview-session.service';
import { AiInterviewTimeDurationPopupComponent } from '@edtech-ui/student-dashboard-v2/ai-interview-time-duration-popup/ai-interview-time-duration-popup.component';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { AiModelName } from '@edtech-ui/constants/ai-model-name.constant';

@Component({
	selector: 'app-step-choose-options',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './step-choose-options.component.html',
	styleUrl: './step-choose-options.component.scss'
})
export class StepChooseOptionsComponent {
	@Output() continue = new EventEmitter<void>();
	@Output() back = new EventEmitter<void>();

	selectedInterviewer = '';
	consent = false;
	interviewers: Interviewer[] = [];
	languages: InterviewLanguage[] = [];
	selectedLanguage = 'en';
	loading = false;
	errorMessage = '';
	availableTime!: number | null;
	selectedDuration: number | null = 5;
	selectDuration(v: number) { this.selectedDuration = v; }


	constructor(
		private toast: ToastrService,
		private session: AiInterviewSessionService,
		private aiInterviewService: AiInterviewServiceService,
		private dialog: MatDialog

	) { }

	durationOptions = [
		{ value: 5, label: '5 min', description: 'Quick practice', premium: false },
		{ value: 10, label: '10 min', description: 'Standard session', premium: false },
		{ value: 20, label: '20 min', description: 'In-depth practice', premium: false },
		{ value: 30, label: '30 min', description: 'Full mock session', premium: false },
	];

	isDurationAvailable(optionValue: number): boolean {
		if (this.availableTime === null) return false;
		return optionValue <= this.availableTime;
	}

	ngOnInit() {
		this.loadInterviewers();
		this.loadLanguages();
		this.availableTime = this.aiInterviewService.getAvailableTime();

		const saved = this.session.value;
		if (saved.duration_minutes) this.selectedDuration = saved.duration_minutes;
		if (saved.voice) this.selectedInterviewer = saved.voice;
		if (saved.preferred_language) this.selectedLanguage = saved.preferred_language;
		if (saved.interviewer_id) {
			const match = this.interviewers.find(i => i.id === saved.interviewer_id);
			if (match) this.selectedInterviewer = match.voice_id;
		}
		this.consent = !!(saved.microphone_status && saved.camera_status);
	}

	loadLanguages() {
		this.session.getLanguages().subscribe({
			next: (res) => {
				if (res.Success && Array.isArray(res.Data)) {
					this.languages = res.Data;
				} else {
					this.languages = [...FALLBACK_INTERVIEW_LANGUAGES];
				}
			},
			error: () => {
				this.languages = [...FALLBACK_INTERVIEW_LANGUAGES];
			}
		});
	}

	selectLanguage(langCode: string) {
		this.selectedLanguage = langCode;
		this.session.update({
			preferred_language: langCode
		});
	}

	loadInterviewers() {
		this.loading = true;
		this.errorMessage = '';

		this.session.getInterviewers().pipe(
			finalize(() => {
				this.loading = false;
			})).subscribe({
				next: (res) => {
					if (res.Success && Array.isArray(res.Data)) {
						this.interviewers = res.Data.map(i => ({
							...i,
							emoji: this.getEmoji(i.gender, i.accent)
						}));
					} else {
						this.errorMessage = res.Message || 'No interviewers found.';
					}
				},
				error: (err) => {
					this.errorMessage = 'Failed to load interviewers. Please try again.';
				}
			});
	}

	getEmoji(gender: string, accent: string): string {
		if (gender === 'female' && accent.includes('Indian')) return '👩‍💼';
		if (gender === 'female' && accent.includes('US')) return '👩‍🔬';
		if (gender === 'male' && accent.includes('Indian')) return '🧑‍💼';
		return '🧑‍💻';
	}

	getInterviewerImage(name: string): string {
		switch (name) {
			case AiModelName.PRIYA_SHARMA:
				return 'assets/images/interview_PriyaSharma.png';
			case AiModelName.SARAH_JOHNSON:
				return 'assets/images/interview_SarahJhonson.png';
			case AiModelName.ARJUN_PATEL:
				return 'assets/images/interview_Arjun Patel.png';
			case AiModelName.MICHAEL_CHEN:
				return 'assets/images/interview_MichaelChen.png';
			default:
				return 'assets/images/student_card_2.svg';
		}
	}

	selectInterviewer(interviewer: any): void {
		this.selectedInterviewer = interviewer.voice_id;
		this.session.update({
			interviewer_id: interviewer.id,
			voice: interviewer.voice_id,
			avatar_language: interviewer.accent.includes('Indian') ? 'en-IN' : 'en-US',
			interviewer_name: interviewer.name,
			interviewer_language: interviewer.accent,
			interviewer_emoji: interviewer.emoji,
			greet_video: interviewer.greet_video,
			video_blink_1: interviewer.video_blink_1,
			video_blink_2: interviewer.video_blink_2,
			video_dubb: interviewer.video_dubb,
			img: interviewer.img
		});
	}

	canContinue(): boolean {
		return !!this.selectedInterviewer && this.consent;
	}

	onContinue() {
		const state = this.session.value;

		if (!this.selectedDuration || !state.interviewer_id || !this.selectedInterviewer || !this.consent) {
			this.toast.warning('Please select an interviewer, duration, and provide consent.');
			return;
		}

		this.session.update({
			duration_minutes: +this.selectedDuration,
			voice: this.selectedInterviewer,
			preferred_language: this.selectedLanguage
		});

		this.continue.emit();
	}

	openAiInterviewDurationDialogBox() {
		this.dialog.open(AiInterviewTimeDurationPopupComponent)
	}

	onDurationClick(value: number) {

		if (!this.isDurationAvailable(value)) {
			this.openAiInterviewDurationDialogBox();
			return;
		}
		this.selectDuration(value);
	}

}
